import { animate, style, transition, trigger } from '@angular/animations';
import {
    AfterViewInit,
    Component,
    ContentChild,
    ElementRef,
    EventEmitter,
    HostListener,
    Input,
    OnInit,
    Output,
    TemplateRef,
    ViewChild
} from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Select } from '@ngxs/store';
import { AutoUnsubscribe } from 'ngx-auto-unsubscribe';
import { ConfirmationService } from 'primeng/api';
import { DialogService } from 'primeng/dynamicdialog';
import { combineLatest, fromEvent, Observable, of, Subject, Subscription } from 'rxjs';
import { catchError, debounceTime, filter, map, shareReplay, startWith, switchMap, tap } from 'rxjs/operators';
import { TenantService } from 'src/app/services/tenant/tenant.service';
import { environment } from 'src/environments/environment';

import { User } from '../../models/user';
import { ConfigurationService } from 'src/app/services/configuration/configuration.service';
import { LayoutService } from '../../services/layout.service';
import { MemberEndpoint } from '../../services/member/member-endpoint.service';
import { MemberSearchService } from '../../services/member/member-search.service';
import { MemberService } from '../../services/member/member.service';
import { UserService } from '../../services/user/user.service';
import { BaseComponent } from '../base.component';
import { IFacetQueryItem, PagingQuery } from '../grid/grid.model';
import { MainConfigService } from '../layouts/main/main-config.service';
import {
    IMemberListItemContext, IMemberSearchRequest, MemberSearchResponseFormatted
} from './../../pages/_searchs/member/member-search.model';
import { IAppSearchBarConfig, SearchCriteriaSyncOptions } from './app-search-bar.models';

@AutoUnsubscribe()
@Component({
    selector: 'app-search-bar',
    templateUrl: './app-search-bar.component.html',
    styleUrls: ['./app-search-bar.component.scss'],
    animations: [
        trigger('topbarActionPanelAnimation', [
            transition(':enter', [style({ opacity: 0, transform: 'scaleY(0.8)' }), animate('.12s cubic-bezier(0, 0, 0.2, 1)', style({ opacity: 1, transform: '*' }))]),
            transition(':leave', [animate('.1s linear', style({ opacity: 0 }))]),
        ]),
    ],
})
export class AppSearchBarComponent extends BaseComponent implements OnInit, AfterViewInit {
    private _globalSearchFilterElement: ElementRef;
    errorMessage: string;
    queryParams$$: Subscription;
    clickInside: boolean;
    currentPageNumber: number;

    @ViewChild('globalSearchFilter', { static: true }) globalSearchFilterElement: ElementRef


    environmentData = environment;

    @ViewChild('searchDialog', { static: false })
    searchDialogElement: ElementRef;

    @ContentChild('itemTemplate') itemTemplate: TemplateRef<any>;

    showResult: boolean;
    searchResultBlocked: boolean;

    memberItems: string[];

    cases: any[];


    @Select(UserService.user)
    userInfo$: Observable<User>;

    memberSearchResult: IMemberListItemContext[];

    private _searchConfig: IAppSearchBarConfig;
    @Input('config') searchConfig: IAppSearchBarConfig;

    @Output() onMemberChangeEvent = new EventEmitter<any>();

    searchValues: string[];

    chipItems: IMemberListItemContext[] = [];

    showOnFocusEvent$$: Subscription;


    globalSearchFilter$: Subscription;
    focusEventToSubject$$: Subscription;

    focused: boolean = false;

    criteria$$: Subscription;

    focusSubject = new Subject<any>();

    focusEvent$$: Subscription;
    blurEvent$$: Subscription;

    initialized: boolean;

    // @ViewChild('resultBlockUI', { static: false }) resultBlockUI: BlockUI;
    // This flag prevents to load the blockUI  until the view is initialized and avoid the error when search bar is loaded by lazy mechanism
    // The blockUI is running the unblock which refer the mask ViewChild but the template is not created yet
    viewInitialized: boolean;


    debounceTime: number = 2500;

    constructor(
        public dialogService: DialogService,
        public memberSearchService: MemberSearchService,
        public tenantService: TenantService,
        public layoutService: LayoutService,
        public memberEndPoint: MemberEndpoint,
        public memberService: MemberService,
        public confirmationService: ConfirmationService,
        public router: Router,
        public route: ActivatedRoute,
        public configService: MainConfigService,
        public userService: UserService,
        private configurationService: ConfigurationService
    ) {
        super();
        this.debounceTime = this.configurationService.searchDebounceTime;
    }

    async ngOnInit() {
        await this.memberSearchService.getMetadata();

        this.memberSearchService.selectedItem$.subscribe(item => {
            this.chipItems = [];
            if (item) {
                this.chipItems = [item];
            }

            this.showResult = false;
        });
        this.searchConfig.inputBoxPlaceHolder = this.searchConfig.inputBoxPlaceHolder ?? 'Search';
    }

    async ngAfterViewInit() {
        setTimeout(async () => {
            this.viewInitialized = true;
            await this.configureGlobalSearch();
        });
    }

    @HostListener('click')
    click() {
        this.clickInside = true;
    }

    @HostListener('document:click', ['$event'])
    processClick(event) {
        if (!this.clickInside) {
            this.showResult = false;
        }

        this.clickInside = false;
    }

    async configureGlobalSearch() {

        if (await this.initializeListeners()) {

            this.showOnFocusEvent$$ = this.focusSubject
                .pipe(
                    switchMap(_ => of(_)),
                    filter((_) => {
                        return this.memberSearchResult && this.memberSearchResult.length > 0;
                    }),
                    debounceTime(this.debounceTime)
                )
                .subscribe((event) => {
                    this.searchDialogElement.nativeElement.display = 'hide';
                });
        }
    }

    openLink(link: string) {
        window.open(link);
    }

    async initializeListeners() {
        try {
            if (!this.initialized) {
                this.globalSearchFilterElement = this.globalSearchFilterElement;

                if (this.globalSearchFilter$) {
                    this.globalSearchFilter$.unsubscribe();
                    this.globalSearchFilter$ = null;
                }

                // focus
                this.focusEventToSubject$$ = fromEvent(this.globalSearchFilterElement.nativeElement, 'focus').pipe(
                    map(_ => this.focused),
                    filter(_ => !this.focused),
                    tap(_ => {
                        this.focused = true;
                    }))
                    .subscribe(this.focusSubject);

                // blur
                const blurEvent$ = fromEvent(this.globalSearchFilterElement.nativeElement, 'blur').pipe(
                    map(_ => this.focused),
                    filter(_ => this.focused),
                    tap(_ => {
                        this.focused = false;
                    }));


                if (this.searchConfig?.syncOption == null || this.searchConfig?.syncOption === SearchCriteriaSyncOptions.always) {
                    this.criteria$$ = this.memberSearchService.criteria$.subscribe(criteria => {
                        this.globalSearchFilterElement.nativeElement.value = criteria;
                        if(this.focused) {
                            this.processSearch();
                        }
                    });
                } else {
                    this.focusEvent$$ = this.focusSubject.subscribe(event => {
                        this.globalSearchFilterElement.nativeElement.value = this.memberSearchService.criteria;
                        if(this.focused) {
                            this.processSearch();
                        }
                    });

                    this.blurEvent$$ = blurEvent$.subscribe(event => {
                        this.globalSearchFilterElement.nativeElement.value = null;
                    });
                }

                const inputEvent = fromEvent(this.globalSearchFilterElement.nativeElement, 'input');

                const deleteEvent = fromEvent(this.globalSearchFilterElement.nativeElement, 'keyup').pipe(
                    startWith(null),
                    filter((event: KeyboardEvent) => {
                        return ['Backspace', 'Delete'].some((key) => !event || event.key === key);
                    })
                );
                const event$ = combineLatest([inputEvent, deleteEvent]).pipe(
                    filter((_) => {
                        return (
                            !this.globalSearchFilterElement.nativeElement.value ||
                            this.globalSearchFilterElement.nativeElement.value.trim().length === 0 ||
                            this.globalSearchFilterElement.nativeElement.value.length > 2
                        );
                    }),
                    debounceTime(this.debounceTime),
                );

                this.globalSearchFilter$ = combineLatest([event$])
                    .pipe(shareReplay(1))
                    .subscribe(([_]) => {
                        this.processSearch();
                    });

                return true;
            } else {
                return false;
            }
        } finally {
            this.initialized = true;
        }
    }


    processSearch() {
        let criteria = this.globalSearchFilterElement.nativeElement.value as string;
        const trimmedCriteria = criteria.trim();
        if (!trimmedCriteria || trimmedCriteria.length === 0) {
            this.memberSearchResult = null;
            this.errorMessage = null;
        } else {
            if (this.memberSearchService.criteria != criteria) {
                this.memberSearchService.criteria = criteria;
            }
            this.searchResultBlocked = true;
            this.searchDialogElement.nativeElement.display = 'show';

            const pagingQuery = new PagingQuery();
            pagingQuery.$first = 0;
            pagingQuery.$rows = 20;
            this.errorMessage = null;

            const req = {
                criteria: this.memberSearchService.criteria,
                columnFilters: [...this.memberSearchService.formatSearchMemberFilters()],
                facets: ['Addresses/State', 'TenantId', 'Repository'].map((facet) => ({ property: facet, maxCount: 100 } as IFacetQueryItem)),
                first: pagingQuery.$first,
                rows: pagingQuery.$rows,
                sort: pagingQuery.$sort,
            } as IMemberSearchRequest;

            this.memberSearchService
                .search(req)
                .pipe(
                    map((response) => {
                        this.currentPageNumber = pagingQuery.$first / pagingQuery.$rows + 1;
                        const result = new MemberSearchResponseFormatted();

                        // sum count of each group
                        result.filteredCount = response.count;
                        result.currentPageSize = 0;

                        response.results.forEach((repositoryGroupResult) => {
                            if (repositoryGroupResult.result) {
                                result.currentPageSize += repositoryGroupResult.result.length;

                                repositoryGroupResult.result.forEach((item) => {
                                    const newItem = {
                                        item,
                                        repoMetadata: { repository: repositoryGroupResult.name, tenantId: repositoryGroupResult.tenantId },
                                        name: repositoryGroupResult.name,
                                        color: repositoryGroupResult.color,
                                    } as IMemberListItemContext;
                                    newItem.smartActions = ((!!this.searchConfig && !!this.searchConfig.itemSmartActionFunc) ? this.searchConfig.itemSmartActionFunc(newItem) : this.memberSearchService.buildSmartActionContext(newItem));
                                    result.items.push(newItem);
                                });
                            }
                        });

                        return result;
                    })
                )
                .pipe(
                    catchError((error) => {
                        console.log(`Error searching member =>`, error);
                        this.errorMessage = MemberSearchService.processMemberSearchError(error);
                        return of(new MemberSearchResponseFormatted(null));
                    }),
                )
                .subscribe(
                    (response) => {
                        this.memberSearchResult = response.items;
                    },
                    (error) => { },
                    () => {
                        this.searchResultBlocked = false;
                    }
                );
        }
    }

    showMemberHistory($event: Event){
        this.memberService.showMemberHistory$.emit(true);
    }
}
